import { useState, useEffect, useCallback, useRef } from 'react'
import {
  UserPlus, ChevronRight, ChevronLeft, Save, User, Calendar,
  MapPin, Flag, GraduationCap, Users, Phone, FileText,
  Building2, HeartPulse, AlertCircle, Check, RotateCcw,
  Loader2, School, ArrowRight, Landmark, CheckCircle2,
  X
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'
import { studentsApi, schoolApi, cyclesApi, levelsApi, classesApi } from '@/services/api'

interface Section { id: number; code: string; label_fr: string; label_en: string }
interface Cycle { id: number; section_id: number; code: string; label_fr: string; label_en: string; order: number }
interface Level { id: number; cycle_id: number; code: string; label_fr: string; label_en: string; order: number }
interface ClassItem { id: number; level_id: number; specialty_id: number; code: string; abbreviation: string; name: string; capacity: number; status: string }
interface AcademicYear { id: number; school_id: number; label: string; start_date: string; end_date: string; is_current: boolean; status: string }
interface SchoolItem { id: number; name: string; acronym: string; city: string; status: string }

function cn(...classes: (string | false | null | undefined)[]) { return classes.filter(Boolean).join(' ') }

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTES LOCALSTORAGE
// ═══════════════════════════════════════════════════════════════════════════════
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
const SELECTED_SCHOOL_ID_KEY = 'lybibal-selected-school-id'
const AUTH_STORAGE_KEY = 'auth-storage'
const SELECTED_YEAR_EVENT = 'lybibal-year-change'

const getStoredSchoolId = (): number | null => {
  try {
    const sid = localStorage.getItem(SELECTED_SCHOOL_ID_KEY)
    if (sid) return Number(sid)
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      if (state?.user?.school_id) return Number(state.user.school_id)
    }
  } catch (e) { console.warn('getStoredSchoolId error:', e) }
  return null
}

const getStoredYearId = (): number | null => {
  try {
    const yid = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    if (yid) return Number(yid)
  } catch (e) { console.warn('getStoredYearId error:', e) }
  return null
}

const getStoredYearLabel = (): string | null => {
  try { return localStorage.getItem(SELECTED_YEAR_LABEL_KEY) } catch (e) { return null }
}

const STEP_LABELS_FR = [
  'Informations personnelles',
  'Informations académiques',
  'Parents & Contacts',
  'Acte de naissance',
  'Santé & Social'
]
const STEP_LABELS_EN = [
  'Personal Information',
  'Academic Information',
  'Parents & Contacts',
  'Birth Certificate',
  'Health & Social'
]

const STEP_ICONS = [User, GraduationCap, Users, FileText, HeartPulse]

// ═══════════════════════════════════════════════════════════════════════════════
// VALIDATION TÉLÉPHONE — uniquement chiffres et +
// ═══════════════════════════════════════════════════════════════════════════════
function sanitizePhone(value: string): string {
  return value.replace(/[^0-9+]/g, '')
}

function isValidPhone(value: string): boolean {
  return /^\+?[0-9]{9,15}$/.test(value)
}

// ═══════════════════════════════════════════════════════════════════════════════
// POPUP DE CONFIRMATION
// ═══════════════════════════════════════════════════════════════════════════════
function SuccessModal({ isOpen, onClose, studentName, matricule, onViewStudents }: {
  isOpen: boolean; onClose: () => void; studentName: string; matricule: string; onViewStudents: () => void
}) {
  const { lang } = useLang()
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-6 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">
            {lang === 'fr' ? 'Élève créé avec succès !' : 'Student created successfully!'}
          </h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-sm text-gray-500">{lang === 'fr' ? 'Nom complet' : 'Full Name'}</p>
                <p className="font-semibold text-gray-800">{studentName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <FileText className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-sm text-gray-500">{lang === 'fr' ? 'Matricule' : 'Registration No.'}</p>
                <p className="font-semibold text-gray-800">{matricule}</p>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={onViewStudents} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200">
              <Users className="w-4 h-4" />
              {lang === 'fr' ? 'Voir les élèves' : 'View Students'}
            </button>
            <button onClick={onClose} className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all border border-gray-200">
              <X className="w-4 h-4" />
              {lang === 'fr' ? 'Nouveau' : 'New'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function StudentCreatePage({ onNavigate }: { onNavigate?: (page: string) => void }) {
  const { lang } = useLang()

  const [schoolId, setSchoolId] = useState<number | null>(getStoredSchoolId)
  const [academicYearId, setAcademicYearId] = useState<number | null>(getStoredYearId)
  const [academicYearName, setAcademicYearName] = useState<string>(getStoredYearLabel() || '')

  const [sections, setSections] = useState<Section[]>([])
  const [cycles, setCycles] = useState<Cycle[]>([])
  const [levels, setLevels] = useState<Level[]>([])
  const [classes, setClasses] = useState<ClassItem[]>([])
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([])
  const [schools, setSchools] = useState<SchoolItem[]>([])
  const [loadingRef, setLoadingRef] = useState(true)

  const [step, setStep] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [createdStudentName, setCreatedStudentName] = useState('')
  const [createdMatricule, setCreatedMatricule] = useState('')

  const [form, setForm] = useState({
    matricule: '', first_name: '', last_name: '', sex: '', date_of_birth: '', place_of_birth: '', nationality: '',
    section_id: '', cycle_id: '', level_id: '', class_id: '',
    father_name: '', mother_name: '', emergency_phone: '',
    birth_certificate_ref: '', civil_status_center: '', birth_certificate_issuer: '',
    handicap: false, handicap_details: '', social_case: false, social_case_details: '',
    school_id: '', academic_year_id: '', user_id: null as number | null,
    previous_school: '', previous_class: '',
    photo_url: '', is_repeating: false,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)

  const schoolIdRef = useRef<number | null>(schoolId)
  const yearIdRef = useRef<number | null>(academicYearId)
  const schoolsRef = useRef<SchoolItem[]>(schools)
  const yearsRef = useRef<AcademicYear[]>(academicYears)

  useEffect(() => { schoolIdRef.current = schoolId }, [schoolId])
  useEffect(() => { yearIdRef.current = academicYearId }, [academicYearId])
  useEffect(() => { schoolsRef.current = schools }, [schools])
  useEffect(() => { yearsRef.current = academicYears }, [academicYears])

  useEffect(() => {
    if (schoolId) setForm(prev => ({ ...prev, school_id: String(schoolId) }))
  }, [schoolId])

  useEffect(() => {
    if (academicYearId) setForm(prev => ({ ...prev, academic_year_id: String(academicYearId) }))
  }, [academicYearId])

  const loadRefData = useCallback(async () => {
    setLoadingRef(true)
    try {
      let detectedSchoolId = schoolIdRef.current
      let detectedSchoolName = ''

      if (!detectedSchoolId) {
        const schoolsData = await schoolApi.getSchools()
        const schoolsList = Array.isArray(schoolsData) ? schoolsData : []
        setSchools(schoolsList)
        schoolsRef.current = schoolsList
        const detectedSchool = schoolsList.find((s: SchoolItem) => s.status === 'active') || schoolsList[0]
        if (detectedSchool) {
          detectedSchoolId = detectedSchool.id
          detectedSchoolName = detectedSchool.name
          setSchoolId(detectedSchoolId)
          schoolIdRef.current = detectedSchoolId
          localStorage.setItem(SELECTED_SCHOOL_ID_KEY, String(detectedSchoolId))
        }
      } else {
        const schoolsData = await schoolApi.getSchools()
        const schoolsList = Array.isArray(schoolsData) ? schoolsData : []
        setSchools(schoolsList)
        schoolsRef.current = schoolsList
        const found = schoolsList.find((s: SchoolItem) => s.id === detectedSchoolId)
        if (found) detectedSchoolName = found.name
      }

      let detectedYearId = yearIdRef.current
      let detectedYearName = academicYearName

      if (detectedSchoolId) {
        const yearsData = await schoolApi.getAcademicYears(detectedSchoolId)
        const yearsList = Array.isArray(yearsData) ? yearsData : []
        setAcademicYears(yearsList)
        yearsRef.current = yearsList

        if (!detectedYearId) {
          const currentYear = yearsList.find((y: AcademicYear) => y.is_current) || yearsList[0]
          if (currentYear) {
            detectedYearId = currentYear.id
            detectedYearName = currentYear.label
            setAcademicYearId(detectedYearId)
            setAcademicYearName(detectedYearName)
            yearIdRef.current = detectedYearId
            localStorage.setItem(SELECTED_YEAR_ID_KEY, String(detectedYearId))
            localStorage.setItem(SELECTED_YEAR_LABEL_KEY, detectedYearName)
          }
        } else {
          const foundYear = yearsList.find((y: AcademicYear) => y.id === detectedYearId)
          if (foundYear) {
            detectedYearName = foundYear.label
            setAcademicYearName(detectedYearName)
          } else {
            const currentYear = yearsList.find((y: AcademicYear) => y.is_current) || yearsList[0]
            if (currentYear) {
              detectedYearId = currentYear.id
              detectedYearName = currentYear.label
              setAcademicYearId(detectedYearId)
              setAcademicYearName(detectedYearName)
              yearIdRef.current = detectedYearId
              localStorage.setItem(SELECTED_YEAR_ID_KEY, String(detectedYearId))
              localStorage.setItem(SELECTED_YEAR_LABEL_KEY, detectedYearName)
            }
          }
        }

        const secData = await schoolApi.getSections(detectedSchoolId)
        setSections(Array.isArray(secData) ? secData : [])
      }

      const [cycData, levData, clsData] = await Promise.allSettled([
        cyclesApi.getAll(), levelsApi.getAll(), classesApi.getAll()
      ])
      const cyclesRaw = cycData.status === 'fulfilled' ? (Array.isArray(cycData.value) ? cycData.value : []) : []
      const levelsRaw = levData.status === 'fulfilled' ? (Array.isArray(levData.value) ? levData.value : []) : []
      const classesRaw = clsData.status === 'fulfilled' ? (Array.isArray(clsData.value) ? clsData.value : []) : []

      const cyclesList = (detectedSchoolId || detectedYearId)
        ? cyclesRaw.filter((c: any) => {
            if (!c) return false
            if (detectedSchoolId && c.school_id !== undefined && Number(c.school_id) !== Number(detectedSchoolId)) return false
            if (detectedYearId && c.academic_year_id !== undefined && Number(c.academic_year_id) !== Number(detectedYearId)) return false
            return true
          })
        : cyclesRaw
      const levelsList = (detectedSchoolId || detectedYearId)
        ? levelsRaw.filter((l: any) => {
            if (!l) return false
            if (detectedSchoolId && l.school_id !== undefined && Number(l.school_id) !== Number(detectedSchoolId)) return false
            if (detectedYearId && l.academic_year_id !== undefined && Number(l.academic_year_id) !== Number(detectedYearId)) return false
            return true
          })
        : levelsRaw
      const classesList = (detectedSchoolId || detectedYearId)
        ? classesRaw.filter((cl: any) => {
            if (!cl) return false
            if (detectedSchoolId && cl.school_id !== undefined && Number(cl.school_id) !== Number(detectedSchoolId)) return false
            if (detectedYearId && cl.academic_year_id !== undefined && Number(cl.academic_year_id) !== Number(detectedYearId)) return false
            return true
          })
        : classesRaw

      setCycles(cyclesList)
      setLevels(levelsList)
      setClasses(classesList)
    } catch {
      toast.error(lang === 'fr' ? 'Erreur chargement des données' : 'Error loading data')
    } finally { setLoadingRef(false) }
  }, [lang])

  useEffect(() => { loadRefData() }, [loadRefData])

  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (!detail?.year_id) return
      const newYearId = Number(detail.year_id)
      const newSchoolId = detail?.school_id !== undefined ? Number(detail.school_id) : null
      if (newYearId && newYearId !== yearIdRef.current) {
        setAcademicYearId(newYearId)
        yearIdRef.current = newYearId
        const label = detail.label || getStoredYearLabel() || ''
        setAcademicYearName(label)
        localStorage.setItem(SELECTED_YEAR_ID_KEY, String(newYearId))
        if (label) localStorage.setItem(SELECTED_YEAR_LABEL_KEY, label)
        toast.info(lang === 'fr' ? 'Année scolaire mise à jour' : 'Academic year updated')
      }
      if (newSchoolId !== null && !isNaN(newSchoolId) && newSchoolId !== schoolIdRef.current) {
        setSchoolId(newSchoolId)
        schoolIdRef.current = newSchoolId
        localStorage.setItem(SELECTED_SCHOOL_ID_KEY, String(newSchoolId))
        toast.info(lang === 'fr' ? 'Établissement mis à jour' : 'School updated')
      }
    }
    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SELECTED_YEAR_ID_KEY) {
        const newYearId = e.newValue ? Number(e.newValue) : null
        if (newYearId && newYearId !== yearIdRef.current) {
          setAcademicYearId(newYearId)
          yearIdRef.current = newYearId
          const label = localStorage.getItem(SELECTED_YEAR_LABEL_KEY) || ''
          setAcademicYearName(label)
          toast.info(lang === 'fr' ? 'Année scolaire mise à jour' : 'Academic year updated')
        }
      }
      if (e.key === SELECTED_SCHOOL_ID_KEY) {
        const newSchoolId = e.newValue ? Number(e.newValue) : null
        if (newSchoolId && newSchoolId !== schoolIdRef.current) {
          setSchoolId(newSchoolId)
          schoolIdRef.current = newSchoolId
          toast.info(lang === 'fr' ? 'Établissement mis à jour' : 'School updated')
        }
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [lang])

  const filteredCycles = form.section_id ? cycles.filter((c: Cycle) => c.section_id === Number(form.section_id)) : cycles
  const filteredLevels = form.cycle_id ? levels.filter((l: Level) => l.cycle_id === Number(form.cycle_id)) : form.section_id ? levels.filter((l: Level) => { const c = cycles.find((cc: Cycle) => cc.id === l.cycle_id); return c && c.section_id === Number(form.section_id) }) : levels
  const filteredClasses = form.level_id ? classes.filter((c: ClassItem) => c.level_id === Number(form.level_id)) : form.cycle_id ? classes.filter((c: ClassItem) => { const lvl = levels.find((l: Level) => l.id === c.level_id); return lvl && lvl.cycle_id === Number(form.cycle_id) }) : classes

  const updateField = (field: string, value: any) => {
    if (field === 'emergency_phone') {
      value = sanitizePhone(value)
    }
    setForm(prev => {
      const next = { ...prev, [field]: value }
      if (field === 'section_id') { next.cycle_id = ''; next.level_id = ''; next.class_id = '' }
      if (field === 'cycle_id') { next.level_id = ''; next.class_id = '' }
      if (field === 'level_id') { next.class_id = '' }
      return next
    })
    if (errors[field]) setErrors(prev => { const e = { ...prev }; delete e[field]; return e })
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error(lang === 'fr' ? 'Veuillez sélectionner une image' : 'Please select an image')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(lang === 'fr' ? "L'image ne doit pas dépasser 5 Mo" : 'Image must not exceed 5 MB')
      return
    }
    setSelectedPhoto(file)
    const reader = new FileReader()
    reader.onloadend = () => setPhotoPreview(reader.result as string)
    reader.readAsDataURL(file)
  }

  const uploadPhoto = async (): Promise<string | null> => {
    if (!selectedPhoto) return form.photo_url || null
    setUploadingPhoto(true)
    try {
      const res = await studentsApi.uploadPhoto(selectedPhoto)
      if (res?.success && res?.photo_url) {
        setForm(prev => ({ ...prev, photo_url: res.photo_url }))
        return res.photo_url
      }
      toast.error(lang === 'fr' ? 'Erreur upload photo' : 'Photo upload error')
      return null
    } catch (err) {
      console.error('Photo upload error:', err)
      toast.error(lang === 'fr' ? 'Erreur upload photo' : 'Photo upload error')
      return null
    } finally {
      setUploadingPhoto(false)
    }
  }

  const validateStep = (s: number): boolean => {
    const newErrors: Record<string, string> = {}
    if (s === 0) {
      if (!form.matricule.trim()) newErrors.matricule = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      if (!form.first_name.trim()) newErrors.first_name = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      if (!form.last_name.trim()) newErrors.last_name = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      if (!form.sex) newErrors.sex = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      if (!form.date_of_birth) newErrors.date_of_birth = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      if (!form.place_of_birth.trim()) newErrors.place_of_birth = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      if (!form.nationality) newErrors.nationality = lang === 'fr' ? 'Champ obligatoire' : 'Required'
    }
    if (s === 1) {
      if (!form.section_id) newErrors.section_id = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      if (!form.cycle_id) newErrors.cycle_id = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      if (!form.level_id) newErrors.level_id = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      if (!form.class_id) newErrors.class_id = lang === 'fr' ? 'Champ obligatoire' : 'Required'
    }
    if (s === 2) {
      if (!form.mother_name.trim()) newErrors.mother_name = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      if (!form.emergency_phone.trim()) {
        newErrors.emergency_phone = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      } else if (!isValidPhone(form.emergency_phone)) {
        newErrors.emergency_phone = lang === 'fr' ? 'Format invalide (ex: +237679738560)' : 'Invalid format (e.g. +237679738560)'
      }
    }
    if (s === 3) {
      if (!form.birth_certificate_ref.trim()) newErrors.birth_certificate_ref = lang === 'fr' ? 'Champ obligatoire' : 'Required'
      if (!form.civil_status_center.trim()) newErrors.civil_status_center = lang === 'fr' ? 'Champ obligatoire' : 'Required'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => { if (validateStep(step)) setStep(prev => Math.min(prev + 1, 4)) }
  const handlePrev = () => setStep(prev => Math.max(prev - 1, 0))

  const handleSubmit = async () => {
    let allValid = true
    for (let s = 0; s <= 4; s++) { if (!validateStep(s)) allValid = false }
    if (!allValid) { toast.error(lang === 'fr' ? 'Veuillez corriger les erreurs' : 'Please fix errors'); return }

    setSubmitting(true)
    try {
      // ═════════════════════════════════════════════════════════════════════════
      // UPLOAD PHOTO si sélectionnée
      // ═════════════════════════════════════════════════════════════════════════
      let photoUrl: string | null = form.photo_url || null
      if (selectedPhoto) {
        photoUrl = await uploadPhoto()
        if (!photoUrl && selectedPhoto) {
          setSubmitting(false)
          return
        }
      }

      // ═════════════════════════════════════════════════════════════════════════
      // PAYLOAD — CORRECTION CRITIQUE
      // 1. Ajouter section_id, cycle_id, level_id pour les enrollments
      // 2. status = "INSCRIT" pour le student (enum PostgreSQL), pas "ACTIVE"
      // 3. enrollment_type = "new" pour l'enrollment
      // ═════════════════════════════════════════════════════════════════════════
      const payload: Record<string, any> = {
        matricule: form.matricule.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        sex: form.sex.toUpperCase(),           // ← M ou F en majuscule
        date_of_birth: form.date_of_birth,
        place_of_birth: form.place_of_birth.trim(),
        nationality: form.nationality || 'Camerounaise',
        mother_name: form.mother_name.trim(),
        emergency_phone: form.emergency_phone.trim(),
        birth_certificate_ref: form.birth_certificate_ref.trim(),
        civil_status_center: form.civil_status_center.trim(),
        birth_certificate_issuer: form.birth_certificate_issuer.trim() || 'Civil Status Registrar',
        handicap: form.handicap,
        social_case: form.social_case,
        // ✅ CORRECTION: status du STUDENT = "INSCRIT" (enum PostgreSQL StudentStatus)
        // Pas "ACTIVE" qui est pour l'enrollment, pas le student
        status: 'INSCRIT',
        school_id: Number(form.school_id),
        academic_year_id: form.academic_year_id ? Number(form.academic_year_id) : null,
        class_id: Number(form.class_id),
        // ✅ CORRECTION: Ajouter section_id, cycle_id, level_id pour les enrollments
        section_id: form.section_id ? Number(form.section_id) : null,
        cycle_id: form.cycle_id ? Number(form.cycle_id) : null,
        level_id: form.level_id ? Number(form.level_id) : null,
        // ✅ CORRECTION: enrollment_type pour l'enrollment
        enrollment_type: 'new',
      }

      // Champs optionnels
      if (form.father_name.trim()) payload.father_name = form.father_name.trim()
      if (form.handicap_details.trim()) payload.handicap_details = form.handicap_details.trim()
      if (form.social_case_details.trim()) payload.social_case_details = form.social_case_details.trim()
      if (form.previous_school.trim()) payload.previous_school = form.previous_school.trim()
      if (form.previous_class.trim()) payload.previous_class = form.previous_class.trim()
      if (form.user_id) payload.user_id = form.user_id
      if (photoUrl) payload.photo_url = photoUrl
      payload.is_repeating = form.is_repeating

      console.log('[StudentCreate] Payload envoyé:', JSON.stringify(payload, null, 2))

      await studentsApi.create(payload)

      setCreatedStudentName(`${form.first_name} ${form.last_name}`)
      setCreatedMatricule(form.matricule)
      setShowSuccess(true)
      toast.success(lang === 'fr' ? 'Élève créé avec succès' : 'Student created')
    } catch (e: any) {
      const respData = e?.response?.data
      console.error('[StudentCreate] Error:', respData)
      if (Array.isArray(respData?.detail)) {
        const details = respData.detail.map((d: any) => `${d.loc?.join('.')}: ${d.msg}`).join('; ')
        toast.error('Validation: ' + details)
      } else if (respData?.errors && Array.isArray(respData.errors)) {
        const details = respData.errors.map((d: any) => `${d.field}: ${d.message}`).join('; ')
        toast.error('Validation: ' + details)
      } else {
        toast.error(respData?.detail || (lang === 'fr' ? 'Erreur création' : 'Create error'))
      }
    } finally { setSubmitting(false) }
  }

  const handleViewStudents = () => {
    setShowSuccess(false)
    if (onNavigate) onNavigate('students')
    else window.location.href = '/students'
  }

  const handleCreateAnother = () => {
    setShowSuccess(false)
    setForm({
      matricule: '', first_name: '', last_name: '', sex: '', date_of_birth: '', place_of_birth: '', nationality: '',
      section_id: '', cycle_id: '', level_id: '', class_id: '',
      school_id: schoolId ? String(schoolId) : '', academic_year_id: academicYearId ? String(academicYearId) : '',
      father_name: '', mother_name: '', emergency_phone: '',
      birth_certificate_ref: '', civil_status_center: '', birth_certificate_issuer: '',
      handicap: false, handicap_details: '', social_case: false, social_case_details: '',
      user_id: null, previous_school: '', previous_class: '',
      photo_url: '', is_repeating: false,
    })
    setErrors({})
    setStep(0)
  }

  const handleReset = () => {
    if (!confirm(lang === 'fr' ? 'Réinitialiser?' : 'Reset?')) return
    handleCreateAnother()
    toast.info(lang === 'fr' ? 'Formulaire réinitialisé' : 'Form reset')
  }

  const labels = lang === 'fr' ? STEP_LABELS_FR : STEP_LABELS_EN
  const detectedSchool = schools.find((s: SchoolItem) => s.id === schoolId)
  const detectedYear = academicYears.find((y: AcademicYear) => y.id === academicYearId)

  if (loadingRef) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-amber-50/20">
      <SuccessModal isOpen={showSuccess} onClose={handleCreateAnother} studentName={createdStudentName} matricule={createdMatricule} onViewStudents={handleViewStudents} />

      <div className="page-header bg-white/80 backdrop-blur-md border-b border-blue-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-200">
              <UserPlus className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="page-title text-2xl font-bold bg-gradient-to-r from-emerald-700 to-emerald-900 bg-clip-text text-transparent">
                {lang === 'fr' ? 'Créer un élève' : 'Create Student'}
              </h1>
              <p className="page-subtitle text-sm text-gray-500 mt-0.5">
                {lang === 'fr' ? 'Inscrire un nouvel élève' : 'Enroll new student'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all border border-gray-200">
              <RotateCcw className="w-4 h-4" />
              {lang === 'fr' ? 'Réinitialiser' : 'Reset'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-5xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between">
            {labels.map((label, i) => {
              const Icon = STEP_ICONS[i]
              const isActive = i === step
              const isDone = i < step
              return (
                <div key={i} className="flex flex-col items-center gap-2 flex-1 relative">
                  {i > 0 && <div className={cn('absolute top-5 right-1/2 w-full h-0.5 -z-10', isDone ? 'bg-emerald-500' : 'bg-gray-200')} />}
                  <button onClick={() => { if (isDone) setStep(i) }} className={cn('w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all border-2', isActive ? 'bg-emerald-600 text-white border-emerald-600 shadow-lg shadow-emerald-200 scale-110' : isDone ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-gray-100 text-gray-400 border-gray-200')}>
                    {isDone ? <Check className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                  </button>
                  <span className={cn('text-xs font-medium text-center hidden md:block max-w-[100px]', isActive ? 'text-emerald-700' : isDone ? 'text-emerald-600' : 'text-gray-400')}>{label}</span>
                </div>
              )
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 bg-gradient-to-r from-emerald-50 to-emerald-100/50 border-b border-emerald-100">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              {(() => { const I = STEP_ICONS[step]; return <I className="w-5 h-5 text-emerald-600" /> })()}
              {labels[step]}
            </h2>
          </div>

          <div className="p-6 space-y-5">
            {step === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Matricule' : 'Reg. No.'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" value={form.matricule} onChange={(e) => updateField('matricule', e.target.value)} placeholder="Ex. 123456789" className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400', errors.matricule ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')} />
                  </div>
                  {errors.matricule && <p className="text-xs text-red-500 mt-1">{errors.matricule}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Prénom' : 'First Name'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" value={form.first_name} onChange={(e) => updateField('first_name', e.target.value)} placeholder="Ex. Franck Landry" className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400', errors.first_name ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')} />
                  </div>
                  {errors.first_name && <p className="text-xs text-red-500 mt-1">{errors.first_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Nom' : 'Last Name'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" value={form.last_name} onChange={(e) => updateField('last_name', e.target.value)} placeholder="Ex. KIBINDE OMBIONYO" className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400', errors.last_name ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')} />
                  </div>
                  {errors.last_name && <p className="text-xs text-red-500 mt-1">{errors.last_name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Sexe' : 'Sex'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select value={form.sex} onChange={(e) => updateField('sex', e.target.value)} className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 appearance-none', errors.sex ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')}>
                      <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                      <option value="M">{lang === 'fr' ? 'Masculin' : 'Male'}</option>
                      <option value="F">{lang === 'fr' ? 'Féminin' : 'Female'}</option>
                    </select>
                  </div>
                  {errors.sex && <p className="text-xs text-red-500 mt-1">{errors.sex}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Date de naissance' : 'Birth Date'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="date" value={form.date_of_birth} onChange={(e) => updateField('date_of_birth', e.target.value)} className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400', errors.date_of_birth ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')} />
                  </div>
                  {errors.date_of_birth && <p className="text-xs text-red-500 mt-1">{errors.date_of_birth}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Lieu de naissance' : 'Birth Place'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" value={form.place_of_birth} onChange={(e) => updateField('place_of_birth', e.target.value)} placeholder="Ex. Douala" className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400', errors.place_of_birth ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')} />
                  </div>
                  {errors.place_of_birth && <p className="text-xs text-red-500 mt-1">{errors.place_of_birth}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Nationalité' : 'Nationality'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Flag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select value={form.nationality} onChange={(e) => updateField('nationality', e.target.value)} className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 appearance-none', errors.nationality ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')}>
                      <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                      <option value="Camerounaise">Camerounaise</option>
                      <option value="Nigériane">Nigériane</option>
                      <option value="Gabonaise">Gabonaise</option>
                      <option value="Tchadienne">Tchadienne</option>
                      <option value="Équato-guinéenne">Équato-guinéenne</option>
                      <option value="Centrafricaine">Centrafricaine</option>
                      <option value="Congolaise">Congolaise</option>
                      <option value="Ivoirienne">Ivoirienne</option>
                      <option value="Sénégalaise">Sénégalaise</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                  {errors.nationality && <p className="text-xs text-red-500 mt-1">{errors.nationality}</p>}
                </div>
                {/* Photo de l'élève */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? "Photo de l'élève" : 'Student Photo'}</label>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-8 h-8 text-gray-300" />
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 cursor-pointer transition-all">
                        <FileText className="w-4 h-4 text-gray-400" />
                        {selectedPhoto ? (lang === 'fr' ? 'Changer la photo' : 'Change photo') : (lang === 'fr' ? 'Choisir une photo' : 'Choose a photo')}
                        <input type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
                      </label>
                      {selectedPhoto && (
                        <p className="text-xs text-gray-500 mt-1">{selectedPhoto.name} ({(selectedPhoto.size / 1024).toFixed(1)} Ko)</p>
                      )}
                      {uploadingPhoto && (
                        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />{lang === 'fr' ? 'Upload en cours...' : 'Uploading...'}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Établissement' : 'School'}</label>
                  <div className="relative">
                    <Landmark className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
                    <input type="text" value={detectedSchool ? `${detectedSchool.name} (${detectedSchool.acronym || detectedSchool.city})` : (lang === 'fr' ? "Non détecté" : "Not detected")} disabled className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm bg-emerald-50/50 border-emerald-200 text-emerald-800 font-medium cursor-not-allowed" />
                  </div>
                  {!schoolId && <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{lang === 'fr' ? 'Aucun établissement sélectionné.' : 'No school selected.'}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Année académique' : 'Academic Year'}</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
                    <input type="text" value={detectedYear ? `${detectedYear.label} ${detectedYear.is_current ? (lang === 'fr' ? ' (Courante)' : ' (Current)') : ''}` : (lang === 'fr' ? 'Aucune année' : 'No year')} disabled className="w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm bg-emerald-50/50 border-emerald-200 text-emerald-800 font-medium cursor-not-allowed" />
                  </div>
                  {schoolId && !academicYearId && <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{lang === 'fr' ? 'Aucune année académique.' : 'No academic year.'}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Section' : 'Section'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <School className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select value={form.section_id} onChange={(e) => updateField('section_id', e.target.value)} className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 appearance-none', errors.section_id ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')}>
                      <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                      {sections.map((s: Section) => (<option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>))}
                    </select>
                  </div>
                  {errors.section_id && <p className="text-xs text-red-500 mt-1">{errors.section_id}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Cycle' : 'Cycle'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select value={form.cycle_id} onChange={(e) => updateField('cycle_id', e.target.value)} disabled={!form.section_id || filteredCycles.length === 0} className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 appearance-none', errors.cycle_id ? 'border-red-300 bg-red-50' : (!form.section_id || filteredCycles.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'border-gray-200 bg-white')}>
                      <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                      {filteredCycles.map((c: Cycle) => (<option key={c.id} value={c.id}>{lang === 'fr' ? c.label_fr : c.label_en}</option>))}
                    </select>
                  </div>
                  {errors.cycle_id && <p className="text-xs text-red-500 mt-1">{errors.cycle_id}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Niveau' : 'Level'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <ArrowRight className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select value={form.level_id} onChange={(e) => updateField('level_id', e.target.value)} disabled={!form.cycle_id || filteredLevels.length === 0} className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 appearance-none', errors.level_id ? 'border-red-300 bg-red-50' : (!form.cycle_id || filteredLevels.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'border-gray-200 bg-white')}>
                      <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                      {filteredLevels.map((l: Level) => (<option key={l.id} value={l.id}>{lang === 'fr' ? l.label_fr : l.label_en}</option>))}
                    </select>
                  </div>
                  {errors.level_id && <p className="text-xs text-red-500 mt-1">{errors.level_id}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Classe' : 'Class'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <select value={form.class_id} onChange={(e) => updateField('class_id', e.target.value)} disabled={!form.level_id || filteredClasses.length === 0} className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 appearance-none', errors.class_id ? 'border-red-300 bg-red-50' : (!form.level_id || filteredClasses.length === 0) ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed' : 'border-gray-200 bg-white')}>
                      <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                      {filteredClasses.map((c: ClassItem) => (<option key={c.id} value={c.id}>{c.name} ({c.abbreviation})</option>))}
                    </select>
                  </div>
                  {errors.class_id && <p className="text-xs text-red-500 mt-1">{errors.class_id}</p>}
                </div>
                {/* Redoublement */}
                <div className="md:col-span-2">
                  <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <input
                      type="checkbox"
                      id="is_repeating"
                      checked={form.is_repeating}
                      onChange={(e) => updateField('is_repeating', e.target.checked)}
                      className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                    />
                    <label htmlFor="is_repeating" className="text-sm text-gray-700 font-medium">
                      {lang === 'fr' ? 'Cet élève redouble la classe' : 'This student is repeating the class'}
                    </label>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Nom du père' : "Father's Name"}</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" value={form.father_name} onChange={(e) => updateField('father_name', e.target.value)} placeholder="Ex. Awang Betrand" className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Nom de la mère' : "Mother's Name"} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" value={form.mother_name} onChange={(e) => updateField('mother_name', e.target.value)} placeholder="Ex. Awang Mary" className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400', errors.mother_name ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')} />
                  </div>
                  {errors.mother_name && <p className="text-xs text-red-500 mt-1">{errors.mother_name}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Téléphone d\'urgence' : 'Emergency Phone'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={form.emergency_phone}
                      onChange={(e) => updateField('emergency_phone', e.target.value)}
                      placeholder="+237679738560"
                      className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400', errors.emergency_phone ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')}
                    />
                  </div>
                  {errors.emergency_phone && <p className="text-xs text-red-500 mt-1">{errors.emergency_phone}</p>}
                  <p className="text-xs text-gray-400 mt-1">{lang === 'fr' ? 'Uniquement chiffres et + (ex: +237679738560)' : 'Digits and + only (e.g. +237679738560)'}</p>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'N° de référence de l\'acte de naissance' : 'Birth Cert Ref'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" value={form.birth_certificate_ref} onChange={(e) => updateField('birth_certificate_ref', e.target.value)} placeholder="Ex. BC-2025-001" className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400', errors.birth_certificate_ref ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')} />
                  </div>
                  {errors.birth_certificate_ref && <p className="text-xs text-red-500 mt-1">{errors.birth_certificate_ref}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? "Centre d\'État Civil" : 'Civil Status Center'} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" value={form.civil_status_center} onChange={(e) => updateField('civil_status_center', e.target.value)} placeholder="Ex. Kumba II Council" className={cn('w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400', errors.civil_status_center ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white')} />
                  </div>
                  {errors.civil_status_center && <p className="text-xs text-red-500 mt-1">{errors.civil_status_center}</p>}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? "Émetteur de l\'acte de naissance" : 'Birth Cert Issuer'}</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" value={form.birth_certificate_issuer} onChange={(e) => updateField('birth_certificate_issuer', e.target.value)} placeholder="Ex. Civil Status Registrar" className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" />
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Handicap' : 'Disability'}</label>
                  <div className="flex items-center gap-3 mt-2">
                    <input type="checkbox" id="handicap" checked={form.handicap} onChange={(e) => updateField('handicap', e.target.checked)} className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500" />
                    <label htmlFor="handicap" className="text-sm text-gray-700">{lang === 'fr' ? 'Cet élève présente un handicap' : 'This student has a disability'}</label>
                  </div>
                  {form.handicap && (
                    <div className="mt-2">
                      <input type="text" value={form.handicap_details} onChange={(e) => updateField('handicap_details', e.target.value)} placeholder={lang === 'fr' ? 'Détails du handicap...' : 'Disability details...'} className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" />
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{lang === 'fr' ? 'Cas social' : 'Social Case'}</label>
                  <div className="flex items-center gap-3 mt-2">
                    <input type="checkbox" id="social_case" checked={form.social_case} onChange={(e) => updateField('social_case', e.target.checked)} className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500" />
                    <label htmlFor="social_case" className="text-sm text-gray-700">{lang === 'fr' ? 'Cet élève est un cas social' : 'This student is a social case'}</label>
                  </div>
                  {form.social_case && (
                    <div className="mt-2">
                      <input type="text" value={form.social_case_details} onChange={(e) => updateField('social_case_details', e.target.value)} placeholder={lang === 'fr' ? 'Détails du cas social...' : 'Social case details...'} className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400" />
                    </div>
                  )}
                </div>
                <div className="md:col-span-2 mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <h3 className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    {lang === 'fr' ? 'Résumé de l\'inscription' : 'Enrollment Summary'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">{lang === 'fr' ? 'Matricule' : 'Reg. No.'}:</span> <span className="font-medium text-gray-800">{form.matricule || '—'}</span></div>
                    <div><span className="text-gray-500">{lang === 'fr' ? 'Nom' : 'Name'}:</span> <span className="font-medium text-gray-800">{form.first_name} {form.last_name}</span></div>
                    <div><span className="text-gray-500">{lang === 'fr' ? 'Sexe' : 'Sex'}:</span> <span className="font-medium text-gray-800">{form.sex === 'M' ? 'Masculin' : form.sex === 'F' ? 'Féminin' : '—'}</span></div>
                    <div><span className="text-gray-500">{lang === 'fr' ? 'Établissement' : 'School'}:</span> <span className="font-medium text-gray-800">{detectedSchool?.name || '—'}</span></div>
                    <div><span className="text-gray-500">{lang === 'fr' ? 'Année académique' : 'Academic Year'}:</span> <span className="font-medium text-gray-800">{detectedYear?.label || '—'}</span></div>
                    <div><span className="text-gray-500">{lang === 'fr' ? 'Classe' : 'Class'}:</span> <span className="font-medium text-gray-800">{classes.find((c: ClassItem) => c.id === Number(form.class_id))?.name || '—'}</span></div>
                    <div><span className="text-gray-500">{lang === 'fr' ? 'Contact' : 'Contact'}:</span> <span className="font-medium text-gray-800">{form.emergency_phone || '—'}</span></div>
                    <div><span className="text-gray-500">{lang === 'fr' ? 'Acte de naissance' : 'Birth Cert.'}:</span> <span className="font-medium text-gray-800">{form.birth_certificate_ref || '—'}</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-5 border-t border-gray-100 flex items-center justify-between">
            <button onClick={handlePrev} disabled={step === 0} className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all', step === 0 ? 'bg-gray-100 text-gray-300 cursor-not-allowed' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200')}>
              <ChevronLeft className="w-4 h-4" />
              {lang === 'fr' ? 'Précédent' : 'Previous'}
            </button>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">{lang === 'fr' ? 'Étape' : 'Step'} {step + 1} / 5</span>
              {step < 4 ? (
                <button onClick={handleNext} className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl text-sm font-medium hover:from-emerald-700 hover:to-emerald-800 transition-all shadow-md shadow-emerald-200">
                  {lang === 'fr' ? 'Suivant' : 'Next'}<ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={submitting} className={cn('flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium transition-all shadow-md', submitting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800 shadow-emerald-200')}>
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />{lang === 'fr' ? 'Enregistrement...' : 'Saving...'}</> : <><Save className="w-4 h-4" />{lang === 'fr' ? 'Enregistrer' : 'Save'}</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}